
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { SupportedLanguage, translations,} from '../translations';

// Interface for translation options
interface TranslationOptions {
    returnObjects?: boolean;
}

type LanguageContextType = {
    language: SupportedLanguage;
    setLanguage: (lang: SupportedLanguage) => void;
    t: (...args: any[]) => any;
};

// Create context with default values
const LanguageContext = createContext<LanguageContextType>({
    language: 'de',
    setLanguage: () => {},
    t: () => '',
});

// Context Provider component
export const LanguageProvider: React.FC<{children: ReactNode}> = ({ children }) => {
    // Load saved language preference or use German as default
    const [language, setLanguage] = useState<SupportedLanguage>(
        () => (localStorage.getItem('language') as SupportedLanguage) || 'de'
    );

    // Save language preference when it changes
    useEffect(() => {
        localStorage.setItem('language', language);
        // Optional: Set HTML lang attribute
        document.documentElement.lang = language;
    }, [language]);

    // Enhanced translation function that supports nested paths and returnObjects
    const t = (...args: any[]): any => {
        try {
            // Check if last argument is options object
            const hasOptions = args.length > 0 &&
                typeof args[args.length - 1] === 'object' &&
                !(args[args.length - 1] instanceof Array);

            const options: TranslationOptions = hasOptions ? args.pop() : {};
            const paths: string[] = args.map(arg => String(arg));

            // Navigate through the translation object
            let result: any = translations[language];

            for (const path of paths) {
                if (!result || typeof result !== 'object') {
                    console.warn(`Translation path error: ${paths.join('.')}`);
                    return String(paths[paths.length - 1]);
                }
                result = result[path];
            }

            // If result is object but returnObjects is false, return path
            if (typeof result === 'object' && !options.returnObjects) {
                console.warn(`Translation result is an object, but returnObjects is not set to true: ${paths.join('.')}`);
                return String(paths[paths.length - 1]);
            }

            // Return empty string if not found
            if (result === undefined) {
                console.warn(`Translation not found: ${paths.join('.')}`);
                return String(paths[paths.length - 1]);
            }

            return result;
        } catch (error) {
            // Fallback if there's an error
            const pathString = args.map(arg => String(arg)).join('.');
            console.warn(`Translation error for: ${pathString}`, error);
            return String(args[args.length - 1] || '');
        }
    };

    // Provide context
    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

// Custom hook for easy access
export const useLanguage = () => useContext(LanguageContext);